import React, { useEffect, useState } from 'react';
import InsurancePatientInfoService from '../../patientInfo/InsurancePatientInfoService';
import apiBaseUrl from '../../../utils/config';

const DEFAULT_DOCTOR_ID = 15;
const DEFAULT_DOCTOR_NAME = 'Default / Not Sure';
const DEFAULT_YEARLY_VISITS = 365;

const InsuranceSection = ({ insuranceData = [], editable = false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [localInsuranceData, setLocalInsuranceData] = useState([]);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    insId: '',
    insuranceName: '',
    subscriber: '',
    activeOptions: [],
    coPay: '',
    notes: '',
    officeId: '',
    officeName: '',
    doctorId: DEFAULT_DOCTOR_ID,
    doctorName: DEFAULT_DOCTOR_NAME,
    yearlyVisitTotal: DEFAULT_YEARLY_VISITS,
    yearlyVisitRemaining: DEFAULT_YEARLY_VISITS,
    yearlyVisitResetYear: new Date().getFullYear(),
  });

  const role = localStorage.getItem('role') || '';
  const normalizedRole = role.replace('ROLE_', '');

  const canDeleteInsurance =
    normalizedRole === 'ADMIN' || normalizedRole === 'MANAGER';

  useEffect(() => {
    setLocalInsuranceData(Array.isArray(insuranceData) ? insuranceData : []);
    setCurrentIndex(0);
    setEditing(false);
  }, [insuranceData]);

  useEffect(() => {
    const handleMessage = (event) => {
      const { inputTarget, value, displayName } = event.data || {};

      if (
        inputTarget === 'insId' ||
        inputTarget === 'insuranceSectionInsId'
      ) {
        setEditForm((prev) => ({
          ...prev,
          insId: value,
          insuranceName: displayName || `Insurance #${value}`,
        }));
      }

      if (
        inputTarget === 'officeId' ||
        inputTarget === 'insuranceSectionOfficeId'
      ) {
        setEditForm((prev) => ({
          ...prev,
          officeId: value,
          officeName: displayName || `Office #${value}`,
        }));
      }

      if (
        inputTarget === 'doctorId' ||
        inputTarget === 'insuranceSectionDoctorId'
      ) {
        setEditForm((prev) => ({
          ...prev,
          doctorId: value,
          doctorName: displayName || `Doctor #${value}`,
        }));
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const getInsuranceRecordId = (insurance) => {
    return (
      insurance?.insptId ||
      insurance?.insurancePatientId ||
      insurance?.id ||
      insurance?.insurancePatientInfoId ||
      null
    );
  };

  const normalizeActiveOptions = (active) => {
    if (!active) {
      return [];
    }

    if (Array.isArray(active)) {
      return active;
    }

    return String(active)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const getOfficeDisplay = (insurance) => {
    if (insurance?.officeName) {
      return insurance.officeName;
    }

    if (insurance?.officeId) {
      return `Office #${insurance.officeId}`;
    }

    return 'Not set';
  };

  const getDoctorDisplay = (insurance) => {
    const doctorId = insurance?.doctorId;

    if (!doctorId) {
      return DEFAULT_DOCTOR_NAME;
    }

    if (Number(doctorId) === DEFAULT_DOCTOR_ID) {
      return DEFAULT_DOCTOR_NAME;
    }

    if (insurance?.doctorName) {
      return insurance.doctorName;
    }

    return `Doctor #${doctorId}`;
  };

  const getYearlyTotal = (insurance) => {
    const total = insurance?.yearlyVisitTotal;
    return Number.isFinite(Number(total)) && Number(total) > 0
      ? Number(total)
      : DEFAULT_YEARLY_VISITS;
  };

  const getYearlyRemaining = (insurance) => {
    const remaining = insurance?.yearlyVisitRemaining;
    return Number.isFinite(Number(remaining)) && Number(remaining) >= 0
      ? Number(remaining)
      : getYearlyTotal(insurance);
  };

  const getYearlyResetYear = (insurance) => {
    const year = insurance?.yearlyVisitResetYear;
    return Number.isFinite(Number(year)) && Number(year) > 0
      ? Number(year)
      : new Date().getFullYear();
  };

  const isYearlyLimitUnlimited = (insurance) => {
    return getYearlyTotal(insurance) === DEFAULT_YEARLY_VISITS;
  };

  const handlePrevious = () => {
    setEditing(false);
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setEditing(false);
    setCurrentIndex((prev) =>
      Math.min(prev + 1, localInsuranceData.length - 1)
    );
  };

  const handleDelete = async () => {
    if (!canDeleteInsurance) {
      alert('Only Admin and Manager users can delete insurance.');
      return;
    }

    const currentInsurance = localInsuranceData[currentIndex];
    const recordId = getInsuranceRecordId(currentInsurance);

    if (!recordId) {
      alert('Could not find the insurance record ID to delete.');
      return;
    }

    if (
      !window.confirm(
        'Are you sure you want to delete this insurance from this patient?'
      )
    ) {
      return;
    }

    try {
      const service = new InsurancePatientInfoService();

      await service.deleteById(recordId);

      const updatedList = localInsuranceData.filter((_, index) => {
        return index !== currentIndex;
      });

      setLocalInsuranceData(updatedList);

      if (updatedList.length === 0) {
        setCurrentIndex(0);
      } else if (currentIndex >= updatedList.length) {
        setCurrentIndex(updatedList.length - 1);
      }

      setEditing(false);

      alert('Insurance deleted successfully.');
    } catch (e) {
      alert('Failed to delete insurance: ' + (e.message || 'Unknown error'));
    }
  };

  const startEdit = () => {
    const currentInsurance = localInsuranceData[currentIndex];

    setEditForm({
      insId: currentInsurance?.insId || '',
      insuranceName: currentInsurance?.insuranceName || '',
      subscriber:
        currentInsurance?.subscriber || currentInsurance?.subscriberId || '',
      activeOptions: normalizeActiveOptions(currentInsurance?.active),
      coPay:
        currentInsurance?.coPay !== undefined &&
        currentInsurance?.coPay !== null
          ? String(currentInsurance.coPay)
          : '',
      notes: currentInsurance?.notes || '',
      officeId: currentInsurance?.officeId || '',
      officeName: currentInsurance?.officeName || '',
      doctorId: currentInsurance?.doctorId || DEFAULT_DOCTOR_ID,
      doctorName: getDoctorDisplay(currentInsurance),
      yearlyVisitTotal: getYearlyTotal(currentInsurance),
      yearlyVisitRemaining: getYearlyRemaining(currentInsurance),
      yearlyVisitResetYear: getYearlyResetYear(currentInsurance),
    });

    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
  };

  const handleEditChange = (key, value) => {
    setEditForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleYearlyTotalChange = (value) => {
    const parsed = parseInt(value || '0', 10);
    const safeValue =
      Number.isFinite(parsed) && parsed > 0
        ? parsed
        : DEFAULT_YEARLY_VISITS;

    setEditForm((prev) => ({
      ...prev,
      yearlyVisitTotal: safeValue,
      yearlyVisitRemaining:
        Number(prev.yearlyVisitTotal) === DEFAULT_YEARLY_VISITS ||
        Number(prev.yearlyVisitRemaining) > safeValue
          ? safeValue
          : prev.yearlyVisitRemaining,
      yearlyVisitResetYear: prev.yearlyVisitResetYear || new Date().getFullYear(),
    }));
  };

  const handleYearlyRemainingChange = (value) => {
    const parsed = parseInt(value || '0', 10);
    const safeValue = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;

    setEditForm((prev) => ({
      ...prev,
      yearlyVisitRemaining: safeValue,
    }));
  };

  const handleActiveOptionChange = (option) => {
    setEditForm((prev) => {
      const currentOptions = prev.activeOptions || [];

      const updatedOptions = currentOptions.includes(option)
        ? currentOptions.filter((item) => item !== option)
        : [...currentOptions, option];

      return {
        ...prev,
        activeOptions: updatedOptions,
      };
    });
  };

  const openInsuranceSearch = () => {
    const popupUrl = `/search-popup?type=Insurance&inputTarget=insuranceSectionInsId&url=${encodeURIComponent(
      `${apiBaseUrl}/insurance/search`
    )}`;

    window.open(popupUrl, 'popup', 'width=700,height=700');
  };

  const openOfficeSearch = () => {
    window.open('/office-search', 'popup', 'width=700,height=700');
  };

  const openDoctorSearch = () => {
    const popupUrl = `/search-popup?type=Doctor&inputTarget=insuranceSectionDoctorId&url=${encodeURIComponent(
      `${apiBaseUrl}/doctorinfo/search/all`
    )}`;

    window.open(popupUrl, 'popup', 'width=700,height=700');
  };

  const useDefaultDoctor = () => {
    setEditForm((prev) => ({
      ...prev,
      doctorId: DEFAULT_DOCTOR_ID,
      doctorName: DEFAULT_DOCTOR_NAME,
    }));
  };

  const saveEdit = async () => {
    const currentInsurance = localInsuranceData[currentIndex];
    const recordId = getInsuranceRecordId(currentInsurance);

    if (!recordId) {
      alert('Could not find the insurance record ID to update.');
      return;
    }

    if (!editForm.insId) {
      alert('Please select an insurance.');
      return;
    }

    if (!editForm.officeId) {
      alert('Please select a default office.');
      return;
    }

    if (!editForm.doctorId) {
      alert('Please select a doctor or use Default / Not Sure.');
      return;
    }

    const yearlyTotal = parseInt(editForm.yearlyVisitTotal || DEFAULT_YEARLY_VISITS, 10);
    const yearlyRemaining = parseInt(editForm.yearlyVisitRemaining || 0, 10);

    if (!yearlyTotal || yearlyTotal <= 0) {
      alert('Yearly visit total must be greater than 0.');
      return;
    }

    if (yearlyRemaining < 0) {
      alert('Yearly visits remaining cannot be negative.');
      return;
    }

    if (yearlyTotal !== DEFAULT_YEARLY_VISITS && yearlyRemaining > yearlyTotal) {
      alert('Yearly visits remaining cannot be greater than yearly visit total.');
      return;
    }

    const updates = {
      insId: parseInt(editForm.insId, 10),
      subscriber: editForm.subscriber.trim(),
      active: editForm.activeOptions.join(','),
      coPay: parseFloat(editForm.coPay || '0'),
      notes: editForm.notes.trim(),
      officeId: parseInt(editForm.officeId, 10),
      doctorId: parseInt(editForm.doctorId, 10),
      yearlyVisitTotal: yearlyTotal,
      yearlyVisitRemaining:
        yearlyTotal === DEFAULT_YEARLY_VISITS
          ? DEFAULT_YEARLY_VISITS
          : yearlyRemaining,
      yearlyVisitResetYear:
        parseInt(editForm.yearlyVisitResetYear || new Date().getFullYear(), 10),
    };

    try {
      const service = new InsurancePatientInfoService();

      const updated = await service.updateFields(recordId, updates);

      const updatedList = localInsuranceData.map((item, index) => {
        if (index !== currentIndex) {
          return item;
        }

        return {
          ...item,
          ...updated,
          insId: updates.insId,
          insuranceName: editForm.insuranceName,
          subscriber: updates.subscriber,
          active: updates.active,
          coPay: updates.coPay,
          notes: updates.notes,
          officeId: updates.officeId,
          officeName: editForm.officeName,
          doctorId: updates.doctorId,
          doctorName: editForm.doctorName,
          yearlyVisitTotal: updates.yearlyVisitTotal,
          yearlyVisitRemaining: updates.yearlyVisitRemaining,
          yearlyVisitResetYear: updates.yearlyVisitResetYear,
        };
      });

      setLocalInsuranceData(updatedList);
      setEditing(false);

      alert('Insurance updated successfully.');
    } catch (e) {
      alert('Failed to update insurance: ' + (e.message || 'Unknown error'));
    }
  };

  if (!localInsuranceData || localInsuranceData.length === 0) {
    return (
      <div className="insurance-section paged-section">
        <h2>Insurance Details</h2>
        <p>No insurance found.</p>
      </div>
    );
  }

  const currentInsurance = localInsuranceData[currentIndex];

  const isActive =
    currentInsurance.active === true ||
    currentInsurance.active === 'true' ||
    currentInsurance.active === 'Yes' ||
    currentInsurance.active === 'YES' ||
    String(currentInsurance.active || '').trim().length > 0;

  const availableOptions = ['visit', 'double', 'acupuncture'];
  const currentIsUnlimited = isYearlyLimitUnlimited(currentInsurance);

  return (
    <div className="insurance-section paged-section">
      <h2>Insurance Details</h2>

      <div className="insurance-card paged-card">
        {!editing ? (
          <>
            <p>
              <strong>Insurance Name:</strong>{' '}
              {currentInsurance.insuranceName || 'N/A'}
            </p>

            <p>
              <strong>Subscriber:</strong>{' '}
              {currentInsurance.subscriber ||
                currentInsurance.subscriberId ||
                'N/A'}
            </p>

            <p>
              <strong>Active / Visit Types:</strong>{' '}
              {isActive ? currentInsurance.active : 'No'}
            </p>

            {currentInsurance.coPay !== undefined &&
              currentInsurance.coPay !== null && (
                <p>
                  <strong>Co-Pay:</strong> {currentInsurance.coPay}
                </p>
              )}

            <p>
              <strong>Yearly Insurance Visits:</strong>{' '}
              {currentIsUnlimited
                ? 'Unlimited / Not Counted'
                : `${getYearlyRemaining(currentInsurance)} remaining / ${getYearlyTotal(
                    currentInsurance
                  )} total`}
            </p>

            {!currentIsUnlimited && (
              <p>
                <strong>Reset Year:</strong>{' '}
                {getYearlyResetYear(currentInsurance)}
              </p>
            )}

            <p>
              <strong>Default Office:</strong>{' '}
              {getOfficeDisplay(currentInsurance)}
            </p>

            <p>
              <strong>Default Doctor:</strong>{' '}
              {getDoctorDisplay(currentInsurance)}
            </p>

            {currentInsurance.notes && (
              <p>
                <strong>Notes:</strong> {currentInsurance.notes}
              </p>
            )}

            {editable && (
              <div className="paged-action-row">
                <button
                  type="button"
                  className="edit-button"
                  onClick={startEdit}
                >
                  Edit
                </button>

                {canDeleteInsurance && (
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleDelete}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <h3>Edit Patient Insurance</h3>

            <div className="input-container">
              <button type="button" onClick={openInsuranceSearch}>
                Search Insurance
              </button>

              <input
                type="text"
                value={editForm.insuranceName || ''}
                readOnly
                placeholder="Selected Insurance"
              />

              <div
                style={{
                  fontSize: '12px',
                  marginBottom: '5px',
                  color: 'gray',
                }}
              >
                Insurance ID: {editForm.insId || 'N/A'}
              </div>
            </div>

            <label>
              Subscriber ID
              <input
                type="text"
                value={editForm.subscriber}
                onChange={(e) =>
                  handleEditChange('subscriber', e.target.value)
                }
              />
            </label>

            <div style={{ marginBottom: '10px' }}>
              <label>Active Status / Visit Types:</label>

              <div>
                {availableOptions.map((option) => (
                  <label key={option} style={{ marginRight: '10px' }}>
                    <input
                      type="checkbox"
                      value={option}
                      checked={editForm.activeOptions.includes(option)}
                      onChange={() => handleActiveOptionChange(option)}
                    />
                    {option}
                  </label>
                ))}
              </div>
            </div>

            <label>
              Co-Pay
              <input
                type="number"
                value={editForm.coPay}
                onChange={(e) => handleEditChange('coPay', e.target.value)}
              />
            </label>

            <div
              style={{
                margin: '12px 0',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                background: '#f9fafb',
              }}
            >
              <h4 style={{ marginTop: 0 }}>Yearly Visit Limit</h4>

              <p style={{ fontSize: '12px', color: '#6b7280' }}>
                Keep total at 365 for unlimited / not counted. Change it only
                when this insurance has a yearly visit cap.
              </p>

              <label>
                Total Yearly Visits
                <input
                  type="number"
                  min="1"
                  value={editForm.yearlyVisitTotal}
                  onChange={(e) => handleYearlyTotalChange(e.target.value)}
                />
              </label>

              <label>
                Yearly Visits Remaining
                <input
                  type="number"
                  min="0"
                  value={editForm.yearlyVisitRemaining}
                  onChange={(e) => handleYearlyRemainingChange(e.target.value)}
                />
              </label>

              <label>
                Reset Year
                <input
                  type="number"
                  min="2000"
                  value={editForm.yearlyVisitResetYear}
                  onChange={(e) =>
                    handleEditChange('yearlyVisitResetYear', e.target.value)
                  }
                />
              </label>
            </div>

            <div className="input-container">
              <button type="button" onClick={openOfficeSearch}>
                Search Default Office
              </button>

              <input
                type="text"
                value={editForm.officeName || ''}
                readOnly
                placeholder="Selected Default Office"
              />

              <div
                style={{
                  fontSize: '12px',
                  marginBottom: '5px',
                  color: 'gray',
                }}
              >
                Office ID: {editForm.officeId || 'N/A'}
              </div>
            </div>

            <div className="input-container">
              <button type="button" onClick={openDoctorSearch}>
                Search Default Doctor
              </button>

              <button
                type="button"
                onClick={useDefaultDoctor}
                style={{ marginLeft: '10px' }}
              >
                Default / Not Sure
              </button>

              <input
                type="text"
                value={editForm.doctorName || ''}
                readOnly
                placeholder="Selected Default Doctor"
              />

              <div
                style={{
                  fontSize: '12px',
                  marginBottom: '5px',
                  color: 'gray',
                }}
              >
                Doctor ID: {editForm.doctorId || 'N/A'}
              </div>
            </div>

            <label>
              Notes
              <textarea
                value={editForm.notes}
                onChange={(e) => handleEditChange('notes', e.target.value)}
              />
            </label>

            <div className="paged-action-row">
              <button type="button" className="save-button" onClick={saveEdit}>
                Save
              </button>

              <button
                type="button"
                className="cancel-button"
                onClick={cancelEdit}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>

      {localInsuranceData.length > 1 && !editing && (
        <div className="paged-controls">
          <button
            type="button"
            className="edit-button"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          >
            Previous
          </button>

          <span>
            {currentIndex + 1} / {localInsuranceData.length}
          </span>

          <button
            type="button"
            className="edit-button"
            onClick={handleNext}
            disabled={currentIndex === localInsuranceData.length - 1}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default InsuranceSection;